import React from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Tooltip, Typography } from '@mui/material';
import { renderDenseIcon } from '../../presentation/cards/iconRegistry';
import type { DenseMetricModel, DenseTagModel, DenseTone } from '../../presentation/cards/types';

interface TripCardDataRailProps {
  primaryMetrics: DenseMetricModel[];
  capacityTags: DenseTagModel[];
  secondaryTags: DenseTagModel[];
  breachTags: DenseTagModel[];
  footerTags: DenseTagModel[];
  footerText?: string;
}

const toneConfig: Record<DenseTone, { base: string; text: string }> = {
  neutral: { base: '#64748b', text: '#334155' },
  info: { base: '#2563eb', text: '#1d4ed8' },
  accent: { base: '#4f46e5', text: '#4338ca' },
  good: { base: '#0f766e', text: '#0f766e' },
  warn: { base: '#b45309', text: '#b45309' },
  danger: { base: '#b42318', text: '#b42318' },
};

const TripCardDataRail: React.FC<TripCardDataRailProps> = ({
  primaryMetrics,
  capacityTags,
  secondaryTags,
  breachTags,
  footerTags,
  footerText,
}) => {
  const theme = useTheme();
  const columnCount = Math.max(primaryMetrics.length, 1);
  const gridTemplateColumns = `repeat(${columnCount}, minmax(0, 1fr))`;

  const renderSecondaryTag = (tag: DenseTagModel) => {
    const tone = toneConfig[tag.tone ?? 'neutral'];
    const content = (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.42,
          px: 0.8,
          py: 0.42,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: alpha(tone.base, 0.16),
          bgcolor: alpha(tone.base, 0.08),
          color: tone.text,
        }}
      >
        {renderDenseIcon(tag.icon, {
          sx: {
            fontSize: 12,
            color: 'inherit',
            flexShrink: 0,
          },
        })}
        <Typography
          component="span"
          sx={{
            fontSize: 10.9,
            fontWeight: 500,
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}
        >
          {tag.label}
        </Typography>
      </Box>
    );

    return (
      <Tooltip key={tag.key} title={tag.tooltip ?? tag.label}>
        {content}
      </Tooltip>
    );
  };

  const renderUtilityTag = (tag: DenseTagModel) => {
    const tone = toneConfig[tag.tone ?? 'neutral'];
    const content = (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.38,
          px: 0.62,
          py: 0.28,
          borderRadius: 1.25,
          border: '1px solid',
          borderColor: alpha(tone.base, 0.16),
          bgcolor: alpha(tone.base, 0.06),
          color: tone.text,
          minHeight: 20,
          maxWidth: tag.maxWidth,
        }}
      >
        {renderDenseIcon(tag.icon, { sx: { fontSize: 11.5, color: 'inherit' } })}
        <Typography
          component="span"
          sx={{
            fontSize: 10.1,
            fontWeight: 500,
            lineHeight: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {tag.label}
        </Typography>
      </Box>
    );

    if (!tag.tooltip) {
      return <React.Fragment key={tag.key}>{content}</React.Fragment>;
    }

    return (
      <Tooltip key={tag.key} title={tag.tooltip}>
        {content}
      </Tooltip>
    );
  };

  return (
    <Box sx={{ display: 'grid', gap: 0.95 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns,
          columnGap: 0.8,
          alignItems: 'center',
          minWidth: 0,
          px: 0.1,
          py: 0.08,
        }}
      >
        {primaryMetrics.map((metric) => {
          const tone = toneConfig[metric.tone ?? 'neutral'];
          const tooltip = metric.tooltip ?? [metric.value, metric.unit].filter(Boolean).join(' ');
          const cell = (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.45,
                minWidth: 0,
                width: '100%',
              }}
            >
              <Box
                sx={{
                  width: 19,
                  height: 19,
                  borderRadius: 1.1,
                  border: '1px solid',
                  borderColor: alpha(tone.base, 0.15),
                  bgcolor: alpha(tone.base, 0.05),
                  color: tone.text,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {renderDenseIcon(metric.icon, {
                  sx: {
                    fontSize: 11.5,
                    color: 'inherit',
                  },
                })}
              </Box>
              <Typography
                sx={{
                  fontSize: 12.4,
                  fontWeight: 500,
                  lineHeight: 1.05,
                  color: theme.palette.mode === 'dark' ? '#f8fafc' : '#0f172a',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                {metric.value}
                {metric.unit ? (
                  <Box
                    component="span"
                    sx={{
                      ml: 0.22,
                      fontSize: 10,
                      fontWeight: 400,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {metric.unit}
                  </Box>
                ) : null}
              </Typography>
            </Box>
          );

          return (
            <Tooltip key={metric.key} title={tooltip}>
              {cell}
            </Tooltip>
          );
        })}
      </Box>

      {capacityTags.length > 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.6,
            px: 0.1,
            py: 0.08,
          }}
        >
          {capacityTags.map((tag) => {
            const tone = toneConfig[tag.tone ?? 'good'];
            const content = (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.38,
                  minWidth: 0,
                  px: 0.62,
                  py: 0.28,
                  borderRadius: 1.25,
                  border: '1px solid',
                  borderColor: alpha(tone.base, 0.16),
                  bgcolor: alpha(tone.base, 0.06),
                  color: tone.text,
                  minHeight: 20,
                }}
              >
                {renderDenseIcon(tag.icon, {
                  sx: {
                    fontSize: 11.25,
                    color: 'inherit',
                    flexShrink: 0,
                  },
                })}
                <Typography
                  component="span"
                  sx={{
                    fontSize: 10.05,
                    fontWeight: 500,
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tag.label}
                </Typography>
              </Box>
            );

            return (
              <Tooltip key={tag.key} title={tag.tooltip ?? tag.label}>
                {content}
              </Tooltip>
            );
          })}
        </Box>
      ) : null}

      {secondaryTags.length > 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.65,
            px: 0.1,
            py: 0.08,
          }}
        >
          {secondaryTags.map(renderSecondaryTag)}
        </Box>
      ) : null}

      {breachTags.length > 0 ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.55, px: 0.1, py: 0.05 }}>
          {breachTags.map(renderUtilityTag)}
        </Box>
      ) : null}

      {(footerTags.length > 0 || footerText) ? (
        <Box sx={{ display: 'grid', gap: 0.6, px: 0.1, py: 0.05 }}>
          {footerTags.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.55 }}>
              {footerTags.map(renderUtilityTag)}
            </Box>
          ) : null}
          {footerText ? (
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: 10.55,
                lineHeight: 1.25,
              }}
            >
              {footerText}
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};

export default TripCardDataRail;
